import { SITE } from '../constants';
import type { TFunction } from 'i18next';
import { getCurrencyFractionDigits, roundToCurrency } from './geoCurrency';

export interface CheckoutDisclaimerPlanDetails {
  price?: number;
  periodPrice?: number;
  currencySymbol?: string;
  duration?: string;
  disclaimer?: string;
}

export interface CheckoutDisclaimerFxDisplay {
  exchangeRate: number;
  currencySymbol: string;
  presentmentCurrency?: string;
}

export interface GetCheckoutDisclaimerHtmlParams {
  planDetails?: CheckoutDisclaimerPlanDetails | null;
  couponPrice?: number;
  t: TFunction;
  fxDisplay?: CheckoutDisclaimerFxDisplay | null;
}

const formatDisclaimerMoney = (
  amount: number,
  currencySymbol: string,
  presentmentCurrency?: string,
): string => {
  if (presentmentCurrency) {
    const digits = getCurrencyFractionDigits(presentmentCurrency);
    const rounded = roundToCurrency(amount, presentmentCurrency);
    return `${currencySymbol}${rounded.toFixed(digits)}`;
  }
  // Whole amounts (COP) stay bare, the rest keep their cents: $2.50, not $2.5.
  const value = Math.max(0, amount);
  return `${currencySymbol}${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

// '3 days' -> { count: 3, unit: 'day' }, 'week' -> { count: 1, unit: 'week' }; null if unrecognised.
const parsePeriod = (period: string) => {
  const [, count = '1', unit] = period.match(/^(\d+)?\s*(day|week|month)s?$/) ?? [];

  return unit ? { count: Number(count), unit } : null;
};

// PLANS durations: trials look like '3 days - 1 month', 'week - month' or '1 day - 1 month',
// subscriptions like '1 month' or '3 months'. Returns null for anything the legal copy doesn't
// describe (yearly or weekly billing, other renewals, typos), so no wrong terms get shown.
export const parseDuration = (duration: string) => {
  const [period, renewal, ...rest] = duration.split(' - ').map(parsePeriod);

  if (!period || rest.length) return null;
  if (renewal === undefined) {
    return period.unit === 'month' ? { trialDays: 0, months: period.count } : null;
  }
  // The trial copy promises a monthly renewal.
  if (period.unit === 'month' || renewal?.unit !== 'month' || renewal.count !== 1) return null;

  return { trialDays: period.count * (period.unit === 'week' ? 7 : 1), months: 1 };
};

export const getCheckoutDisclaimerHtml = ({
  planDetails,
  couponPrice = 0,
  t,
  fxDisplay = null,
}: GetCheckoutDisclaimerHtmlParams): string => {
  // No plan selected yet.
  if (!planDetails?.duration) return '';

  const { price = 0, periodPrice = 0, duration } = planDetails;
  const introPrice = +(periodPrice - couponPrice).toFixed(2);
  // OCS checkouts quote prices in the shopper's currency.
  const currencySymbol = fxDisplay?.currencySymbol ?? planDetails.currencySymbol ?? '$';
  const rate = fxDisplay?.exchangeRate;
  const formatPrice = (amount: number) =>
    formatDisclaimerMoney(
      typeof rate === 'number' && rate > 0 ? amount / rate : amount,
      currencySymbol,
      fxDisplay?.presentmentCurrency,
    );
  const fullPrice = formatPrice(price);
  const terms = parseDuration(duration);

  if (!terms) {
    console.warn(`No checkout disclaimer copy for plan duration "${duration}"`);
    return t('legal_disclaimer', {
      price: fullPrice,
      duration: planDetails.disclaimer ? t(planDetails.disclaimer) : '',
      site: SITE,
    });
  }

  if (terms.trialDays) {
    return t('disclaimer_trial', {
      introPrice: formatPrice(introPrice),
      trialDays: terms.trialDays,
      fullPrice,
    });
  }

  const { months } = terms;

  if (introPrice < price) {
    return t(months === 1 ? 'disclaimer_1_month' : 'disclaimer_n_months', {
      introPrice: formatPrice(introPrice),
      fullPrice,
      count: months,
    });
  }

  // No coupon (e.g. once the timer expires): the first period is full price, so there's no discount to disclose.
  return t(months === 1 ? 'disclaimer_1_month_full_price' : 'disclaimer_n_months_full_price', {
    fullPrice,
    count: months,
  });
};
